import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { FaFacebook, FaLinkedin, FaInstagram } from "react-icons/fa";
import "./HomePage.css";

// Try this import path (adjust based on your actual structure)
import heroImage from "../assets/hero-image.jpg"; // If image is in src/assets

// Import testimonial images
import johnImage from "../assets/john.jpg";
import maryImage from "../assets/mary.jpg";
import alexImage from "../assets/alex.jpg";

// Dummy feature icons (replace with SVGs if needed)
const features = [
  { icon: "📝", title: "Plagiarism-Free", desc: "All papers are unique and checked." },
  { icon: "👨‍🎓", title: "Expert Writers", desc: "Professional academic writers." },
  { icon: "⏱️", title: "On-time Delivery", desc: "Guaranteed deadlines." },
  { icon: "🔒", title: "Secure Payment", desc: "Safe and encrypted transactions." },
];

const steps = [
  { step: 1, title: "Place Order", desc: "Fill in the details of your assignment." },
  { step: 2, title: "Writer Assigned", desc: "We match your paper with an expert." },
  { step: 3, title: "Receive Paper", desc: "Download your paper and review." },
];

// Services data - matching the navbar Writing Help links
const services = [
  { name: "Essay", path: "/writing-help/essay" },
  { name: "Research Paper", path: "/writing-help/research-paper" },
  { name: "Thesis", path: "/writing-help/thesis" },
  { name: "Dissertation", path: "/writing-help/dissertation" },
  { name: "Assignment", path: "/writing-help/assignment" },
  { name: "Book Report", path: "/writing-help/book-report" },
  { name: "Speech", path: "/writing-help/speech" },
  { name: "Personal Statement", path: "/writing-help/personal-statement" },
  { name: "Literature Review", path: "/writing-help/literature-review" },
  { name: "Coursework", path: "/writing-help/coursework" },
  { name: "Lab Report", path: "/writing-help/lab-report" },
  { name: "Homework", path: "/writing-help/homework" },
  { name: "Case Study", path: "/writing-help/case-study" },
  { name: "Capstone Project", path: "/writing-help/capstone-project" },
  { name: "AI Humanizer", path: "/writing-help/AI-humanizer" },
  { name: "Editing & Proofreading", path: "/writing-help/proofreading" },
  { name: "Referencing & Citations", path: "/writing-help/Referencing" },
];

// Updated testimonials with local images
const testimonials = [
  { name: "John D.", text: "Excellent service! Highly recommended.", img: johnImage },
  { name: "Mary S.", text: "My paper was perfect and delivered on time.", img: maryImage },
  { name: "Alex P.", text: "Professional and reliable writers!", img: alexImage },
];

function HomePage() {
  const [heroHeight, setHeroHeight] = useState("auto");
  const imageRef = useRef(null);

  useEffect(() => {
    // Function to set hero height based on image height
    const updateHeroHeight = () => {
      if (imageRef.current) {
        const imageHeight = imageRef.current.offsetHeight;
        setHeroHeight(`${imageHeight}px`);
      }
    };

    // Initial update
    updateHeroHeight();

    // Update on window resize
    window.addEventListener('resize', updateHeroHeight);

    // Cleanup
    return () => {
      window.removeEventListener('resize', updateHeroHeight);
    };
  }, []);

  // Handle image load to update height
  const handleImageLoad = () => {
    if (imageRef.current) {
      const imageHeight = imageRef.current.offsetHeight;
      setHeroHeight(`${imageHeight}px`);
    }
  };

  return (
    <div className="home-page">

      {/* ===== HERO SECTION ===== */}
      <section className="hero" style={{ height: heroHeight }}>
        <div className="hero-content">
          {/* Image on the LEFT - takes 1/4 width, full height */}
          <div className="hero-image-container">
            <div className="hero-image-wrapper">
              <img 
                ref={imageRef}
                src={heroImage} 
                alt="Research and Writing" 
                className="hero-image"
                onLoad={handleImageLoad}
                onError={(e) => {
                  // Fallback if image doesn't load
                  e.target.src = "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                  handleImageLoad(); // Update height with fallback image
                }}
              />
            </div>
          </div>
          
          {/* Text content on the RIGHT - takes 3/4 width */}
          <div className="hero-text">
            <h1>Professional Research & Writing Help</h1>
            <p>Get top-quality papers, plagiarism-free and delivered on time by expert academic writers.</p>
            <div className="hero-buttons">
              <Link to="/order" className="btn-primary">Order Now</Link>
              <Link to="/Samples" className="btn-secondary">View Samples</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="features">
        <div className="section-container">
          {features.map((f, idx) => (
            <div key={idx} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="how-it-works">
        <div className="section-container">
          {steps.map((s, idx) => (
            <div key={idx} className="step-card">
              <div className="step-circle">{s.step}</div>
              <h4>{s.title}</h4>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="testimonials">
        <div className="section-container">
          {testimonials.map((t, idx) => (
            <div key={idx} className="testimonial-card">
              <img src={t.img} alt={t.name} />
              <p>"{t.text}"</p>
              <h5>{t.name}</h5>
            </div>
          ))}
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="services">
        <div className="section-container services-grid">
          {services.map((service, idx) => (
            <Link 
              key={idx} 
              to={service.path} 
              className="service-card"
            >
              {service.name}
            </Link>
          ))}
        </div>
      </section>

      {/* ===== CTA STRIP ===== */}
      <section className="cta-strip">
        <h2>Ready to Get Started?</h2>
        <Link to="/order" className="btn-primary">Order Now</Link>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-col">
            <h4>Quick Links</h4>
            <Link to="/">Home</Link>
            <Link to="/writing-help">Writing Help</Link>
            <Link to="/Samples">Samples</Link>
            <Link to="/plagiarism">Plagiarism Checker</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/writers">For Writers</Link>
          </div>
          <div className="footer-col">
            <h4>Services</h4>
            <Link to="/writing-help/essay">Essay Writing</Link>
            <Link to="/writing-help/research-paper">Research Paper</Link>
            <Link to="/writing-help/thesis">Thesis</Link>
            <Link to="/writing-help/dissertation">Dissertation</Link>
            <Link to="/writing-help/assignment">Assignment</Link>
            <Link to="/writing-help/AI-humanizer">AI Humanizer</Link>
            <Link to="/writing-help/proofreading">Editing & Proofreading</Link>
          </div>
          <div className="footer-col">
            <h4>Resources</h4>
            <Link to="/faq">FAQ</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/pricing">Pricing</Link>
          </div>
          <div className="footer-col">
            <h4>Contact</h4>
            <a href="mailto:researchwriting.infor@gmail.com" className="contact-pill"> researchwriting.infor@gmail.com </a>
            <a href="tel:+254718583950" className="contact-pill">
              <span className="contact-icon">📞</span>
              +254 718 583 950
            </a>
            <span className="contact-pill">
              <span className="contact-icon">🕒</span>
              Mon-Fri 9am-6pm EST
            </span>
            <div className="social-links">
              <a
                href="https://web.facebook.com/Researchwriting.infor/directory_links"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                <FaFacebook /> Facebook
              </a>
              <a
                href="http://www.linkedin.com/in/research-writing-9133403b4"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                <FaLinkedin /> LinkedIn
              </a>
              <a
                href="https://www.instagram.com/researchwriting.infor/?hl=en#"
                target="_blank"
                rel="noopener noreferrer"
                className="social-link"
              >
                <FaInstagram /> Instagram
              </a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Research Writing. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
